import { store } from '@/store'
import { defineStore } from 'pinia'
import { getAdminAccessToken, removeAdminToken } from '@/utils/auth'
import { CACHE_KEY, useCache, deleteUserCache } from '@/hooks/web/useCache'
import { getInfo, loginOut } from '@/api/login'
import type { AppCustomRouteRecordRaw } from '@/router/types'

const { wsCache } = useCache()

interface UserVO {
  id: number
  avatar: string
  nickname: string
  deptId: number
}

interface UserInfoVO {
  // USER 缓存
  permissions: Set<string>
  roles: string[]
  isSetUser: boolean
  user: UserVO
  /** 当前租户类型：1=内部 2=外部 */
  tenantType?: number
}

interface AuthPermissionInfoRespVO {
  permissions: string[]
  roles: string[]
  user: UserVO
  menus: AppCustomRouteRecordRaw[]
  tenantType?: number
}

export const useUserStore = defineStore('admin-user', {
  state: (): UserInfoVO => ({
    permissions: new Set<string>(),
    roles: [],
    isSetUser: false,
    user: {
      id: 0,
      avatar: '',
      nickname: '',
      deptId: 0
    },
    tenantType: undefined
  }),
  getters: {
    getPermissions(): Set<string> {
      return this.permissions
    },
    getRoles(): string[] {
      return this.roles
    },
    getIsSetUser(): boolean {
      return this.isSetUser
    },
    getUser(): UserVO {
      return this.user
    },
    getTenantType(): number | undefined {
      return this.tenantType
    },
    /** 外部租户（含其上的超管）隐藏仅内部菜单 */
    isExternalTenant(): boolean {
      return this.tenantType === 2
    }
  },
  actions: {
    async setUserInfoAction() {
      if (!getAdminAccessToken()) {
        this.resetState()
        return null
      }
      let userInfo: AuthPermissionInfoRespVO | null = wsCache.get(CACHE_KEY.USER)
      try {
        userInfo = await getInfo()
      } catch (error) {
        // 无缓存且拉权限失败（常见：过期 token）：清登录态，避免 router.isReady 卡死 → 独立窗白屏
        if (!userInfo) {
          removeAdminToken()
          deleteUserCache()
          this.resetState()
          return null
        }
        // 有缓存：沿用旧菜单，保证仍可进入系统
      }
      if (!userInfo) {
        this.resetState()
        return null
      }
      this.permissions = new Set(userInfo.permissions || []) // 兜底为 [] https://t.zsxq.com/xCJew
      this.roles = userInfo.roles
      this.user = userInfo.user
      this.tenantType = userInfo.tenantType
      this.isSetUser = true
      wsCache.set(CACHE_KEY.USER, userInfo)
      wsCache.set(CACHE_KEY.ROLE_ROUTERS, userInfo.menus)
    },
    async setUserAvatarAction(avatar: string) {
      const userInfo = wsCache.get(CACHE_KEY.USER)
      // NOTE: 是否需要像`setUserInfoAction`一样判断`userInfo != null`
      this.user.avatar = avatar
      userInfo.user.avatar = avatar
      wsCache.set(CACHE_KEY.USER, userInfo)
    },
    async setUserNicknameAction(nickname: string) {
      const userInfo = wsCache.get(CACHE_KEY.USER)
      // NOTE: 是否需要像`setUserInfoAction`一样判断`userInfo != null`
      this.user.nickname = nickname
      userInfo.user.nickname = nickname
      wsCache.set(CACHE_KEY.USER, userInfo)
    },
    async loginOut() {
      await loginOut()
      removeAdminToken()
      deleteUserCache() // 删除用户缓存
      this.resetState()
    },
    resetState() {
      this.permissions = new Set<string>()
      this.roles = []
      this.isSetUser = false
      this.tenantType = undefined
      this.user = {
        id: 0,
        avatar: '',
        nickname: '',
        deptId: 0
      }
    }
  }
})

export const useUserStoreWithOut = () => {
  return useUserStore(store)
}
